// Simple Spotify service used by both the Playlists page and the homepage carousel
// NOTE: For production, move CLIENT_ID/CLIENT_SECRET to a Supabase Edge Function using secrets.

export interface Track {
  id: string;
  name: string;
  artist: string;
  previewUrl: string | null;
  spotifyUrl: string;
  durationMs: number;
}

export interface Playlist {
  id?: string;
  name: string;
  description: string;
  imageUrl: string;
  spotifyUrl: string;
  songs: number;
  duration: string;
  tracks?: Track[];
}

const CLIENT_ID = 'ccf1ba97bfdc445db4c0bbbcf53005fb';
const CLIENT_SECRET = 'c078e6bbd57147d29d60032db5c431a5';
export const SPOTIFY_USER_ID = '31hhpjftvvqrx4ocrhwy6uv22fba';

async function getAccessToken(): Promise<string> {
  const res = await fetch('https://accounts.spotify.com/api/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'Basic ' + btoa(`${CLIENT_ID}:${CLIENT_SECRET}`),
    },
    body: 'grant_type=client_credentials',
  });
  if (!res.ok) throw new Error('Failed to get Spotify access token');
  const json = await res.json();
  return json.access_token as string;
}

function formatDuration(totalMs: number) {
  const hours = Math.floor(totalMs / 3600000);
  const minutes = Math.floor((totalMs % 3600000) / 60000);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export async function fetchUserPlaylists(userId: string, limit: number = 6): Promise<Playlist[]> {
  const accessToken = await getAccessToken();

  const playlistsRes = await fetch(
    `https://api.spotify.com/v1/users/${userId}/playlists?limit=${Math.max(limit, 1)}`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  if (!playlistsRes.ok) throw new Error('Failed to fetch playlists');
  const playlistsData = await playlistsRes.json();

  const items: any[] = playlistsData.items || [];

  const detailed = await Promise.all(
    items.slice(0, limit).map(async (playlist) => {
      const detailRes = await fetch(`https://api.spotify.com/v1/playlists/${playlist.id}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!detailRes.ok) return null;
      const detail = await detailRes.json();
      
      // Extract track information with preview URLs
      const tracks: Track[] = (detail.tracks.items || [])
        .filter((item: any) => item.track && !item.track.is_local)
        .slice(0, 5) // Only get first 5 tracks for preview
        .map((item: any) => ({
          id: item.track.id,
          name: item.track.name,
          artist: item.track.artists?.[0]?.name || 'Unknown Artist',
          previewUrl: item.track.preview_url,
          spotifyUrl: item.track.external_urls.spotify,
          durationMs: item.track.duration_ms || 0,
        }));

      const totalMs = (detail.tracks.items || []).reduce((sum: number, item: any) => sum + (item.track?.duration_ms || 0), 0);
      const imageUrl = playlist.images?.[0]?.url || 'https://i.scdn.co/image/ab67706c0000bebb4cf969baf1b68dd3ef894682';
      const description = playlist.description || 'A curated playlist by Everhouse';
      const p: Playlist = {
        id: playlist.id,
        name: playlist.name,
        description,
        imageUrl,
        spotifyUrl: playlist.external_urls.spotify,
        songs: detail.tracks.total,
        duration: formatDuration(totalMs),
        tracks,
      };
      return p;
    })
  );

  return detailed.filter(Boolean) as Playlist[];
}

export function getSpotifyDeepLink(spotifyUrl: string): string {
  const playlistId = spotifyUrl.split('/playlist/')[1]?.split('?')[0];
  if (playlistId) {
    return `spotify:playlist:${playlistId}`;
  }
  return spotifyUrl;
}

export function openSpotify(spotifyUrl: string): void {
  const deepLink = getSpotifyDeepLink(spotifyUrl);
  
  // Try to open Spotify app first
  const link = document.createElement('a');
  link.href = deepLink;
  link.click();
  
  // Fallback to web after a short delay
  setTimeout(() => {
    window.open(spotifyUrl, '_blank');
  }, 1000);
}
